import { ref } from 'vue'
import { getUnreadCount } from '@/api/modules/notification'

/** 管理分类统计；scope 返回当前账号/公司/服务端身份，失效响应不写入状态。 */
class NotificationUnreadState {
  readonly unreadCount = ref(0)
  readonly breakdown = ref<NotificationUnreadRes | null>(null)
  readonly statsReady = ref(false)
  readonly statsLoading = ref(false)
  readonly statsError = ref('')
  readonly classificationSupported = ref<boolean | null>(null)
  private sequence = 0
  private mutating = false
  private pending: Promise<boolean> | null = null
  /** 保存状态依赖；不触发接口请求。 */
  constructor(private readonly scope: () => string) {}

  /** 使旧轮询失效，不清空已有数字。 */
  invalidate = () => {
    this.sequence++
    this.pending = null
    this.statsLoading.value = false
  }

  /** 清理身份相关统计，首次失败不能被视为已取得零未读。 */
  reset = () => {
    this.invalidate()
    this.mutating = false
    this.unreadCount.value = 0
    this.breakdown.value = null
    this.statsReady.value = false
    this.statsError.value = ''
    this.classificationSupported.value = null
  }

  /** 刷新全局统计；写操作期间拒绝旧查询覆盖乐观状态。 */
  fetchUnreadCount = (): Promise<boolean> => {
    if (!this.scope() || this.mutating) return Promise.resolve(false)
    if (this.pending) return this.pending
    const request = this.loadUnreadCount().finally(() => {
      if (this.pending === request) this.pending = null
    })
    this.pending = request
    return request
  }

  /** 执行唯一在途统计；序号防止关闭、身份切换和写操作后的旧回包。 */
  private loadUnreadCount = async () => {
    const currentScope = this.scope()
    const request = ++this.sequence
    this.statsLoading.value = true
    try {
      const result = await getUnreadCount(true)
      if (request !== this.sequence || currentScope !== this.scope()) return false
      this.unreadCount.value = result.count
      this.statsReady.value = true
      this.statsError.value = ''
      this.classificationSupported.value =
        result.classificationVersion === 1 && !!result.groups && !!result.messageCategories
      this.breakdown.value = this.classificationSupported.value ? result : null
      return this.classificationSupported.value
    } catch {
      if (request === this.sequence && currentScope === this.scope()) {
        this.statsError.value = '未读统计未更新，请稍后重试；首次使用请确认服务端分类能力已就绪'
      }
      return false
    } finally {
      if (request === this.sequence && currentScope === this.scope())
        this.statsLoading.value = false
    }
  }

  /** 开始已读写操作；返回完整统计快照用于失败回滚。 */
  beginMutation = () => {
    this.invalidate()
    this.mutating = true
    return {
      count: this.unreadCount.value,
      breakdown: this.breakdown.value
        ? (JSON.parse(JSON.stringify(this.breakdown.value)) as NotificationUnreadRes)
        : null,
    }
  }

  /** 完成写操作；snapshot 只在失败时传入，成功以服务端重新统计为准。 */
  finishMutation = (snapshot?: ReturnType<NotificationUnreadState['beginMutation']>) => {
    if (snapshot) {
      this.unreadCount.value = snapshot.count
      this.breakdown.value = snapshot.breakdown
    }
    this.mutating = false
    return this.fetchUnreadCount()
  }

  /** 按 notification 乐观扣减；未传表示所有分类已读，最终仍以重查结果为准。 */
  applyRead = (notification?: AppNotification) => {
    this.unreadCount.value = notification ? Math.max(0, this.unreadCount.value - 1) : 0
    const stats = this.breakdown.value
    if (!stats?.groups || !stats.messageCategories) return
    if (!notification) {
      stats.groups = { message: 0, todo: 0 }
      for (const key of Object.keys(stats.messageCategories) as Exclude<
        NotificationCategory,
        'all'
      >[])
        stats.messageCategories[key] = 0
    } else {
      const todo = notification.type === 'ai' || notification.type === 'task'
      const group = todo ? 'todo' : 'message'
      stats.groups[group] = Math.max(0, stats.groups[group] - 1)
      if (!todo) {
        const event = notification.eventType as Exclude<NotificationCategory, 'all'>
        const category =
          notification.type === 'system' &&
          Object.prototype.hasOwnProperty.call(stats.messageCategories, event)
            ? event
            : 'other'
        stats.messageCategories[category] = Math.max(0, stats.messageCategories[category] - 1)
      }
    }
    stats.count = this.unreadCount.value
  }
}

/** 创建隔离的通知状态；参数分别提供当前身份及相关数据状态。 */
export function useNotificationUnread(scope: () => string) {
  const state = new NotificationUnreadState(scope)
  return {
    unreadCount: state.unreadCount,
    breakdown: state.breakdown,
    statsReady: state.statsReady,
    statsLoading: state.statsLoading,
    statsError: state.statsError,
    classificationSupported: state.classificationSupported,
    fetchUnreadCount: state.fetchUnreadCount,
    invalidate: state.invalidate,
    reset: state.reset,
    beginMutation: state.beginMutation,
    finishMutation: state.finishMutation,
    applyRead: state.applyRead,
  }
}
